import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { Maybe } from '../../core';
import { notifyError, notifySuccess } from '../../services/notifications';
import { Store } from '../../store/store';
import { Message, StoreNode } from '../../store';
import { WindowState } from '../overlays/windowState';
import { JobModel, JobVideoTypeList } from '../../entities';
import { input, inputGroup } from '../input';
import { JobType, UpdateJobInput } from '@clipr/lib';
import Routes from '../../routes';

export class VideoInformationWindowState extends StoreNode {
  readonly nodeType = 'VideoInformationWindow';

  constructor(store: Store) {
    super(store);
    makeObservable<this, 'windowListener'>(this, {
      windowListener: action,
      isLoading: observable,
      isSubmitting: observable,
      jobId: observable,
      job: observable,
      streamedOnText: computed,
      init: action,
      handleFailed: action,
      handleSuccess: action,
      submit: action,
      open: action,
      clearState: action,
      close: action,
      onTransitionEnd: action,
    });

    this.window.listen(this.windowListener);
  }

  readonly window = new WindowState(this.store);

  private windowListener = (msg: Message<WindowState>) => {
    switch (msg.type) {
      case 'close':
      case 'outsideClick':
        if (this.isLoading || this.inputGroup.isSubmitting)
          return;
        this.close();
        break;
    }
  };

  readonly titleInput = input(this, {
    name: 'title',
    isRequired: true,
  });

  readonly videoTypeInput = input(this, {
    name: 'videoType',
    showStatusMessage: true,
    selectorItems: JobVideoTypeList,
  });

  readonly inputGroup = inputGroup(this, {
    name: 'window',
    inputs: [
      this.titleInput,
      this.videoTypeInput
    ]
  });

  isLoading: boolean = false;
  isSubmitting: boolean = false;
  jobId: Maybe<string>;
  job: Maybe<JobModel>;

  get streamedOnText() {
    return this.job?.streamedOnLabel;
  }

  async init() {
    this.isLoading = true;

    const job = this.store.jobManager.maybeGetJob(this.jobId!);
    this.job = job;
    if (this.job) {
      this.handleSuccess();
    }

    if (!this.job) {
      const [job, err] = await this.store.jobManager.apiFetchJob(this.jobId!);

      runInAction(() => {
        if (err) {
          this.handleFailed();
        } else {
          this.job = job;
        }
        this.handleSuccess();
      });
    }
  }

  handleFailed() {
    this.close();
    notifyError(this, 'Content no longer exists.');
    this.isLoading = false;
  }

  handleSuccess() {
    this.titleInput.value = this.job?.title || null;
    this.videoTypeInput.value = this.job?.videoType || null;
    this.isLoading = false;
  }

  async submit() {
    if (!this.jobId) return;

    const input: UpdateJobInput = {
      id: this.jobId,
      videoType: this.videoTypeInput.value as JobType || undefined,
      title: this.titleInput.value || undefined,
    };
    this.isLoading = true;

    const [, err] = await this.store.jobManager.apiUpdateJob(input);
    if (err) {
      runInAction(() => this.isLoading = false);
      return notifyError(this, 'Failed to update.');
    }

    runInAction(() => this.isLoading = false);
    notifySuccess(this, 'Updated successfully.');
    this.close('jobUpdated');
  }

  open({ jobId }: { jobId: string }) {
    this.dispatch('Overlays', 'openWindow', { name: 'VideoInformationWindow' });
    this.emit('open');
    this.jobId = jobId;
    this.init();
  }

  clearState() {
    if (this.store.history.location.pathname === Routes.trainerDashboardFailedJob(this.jobId!)) {
      this.store.history.replace(Routes.trainerDashboard());
    }
    this.jobId = null;
    this.titleInput.value = null;
    this.videoTypeInput.value = null;
  }

  close(msg: string = 'close') {
    this.emit(msg);
    this.dispatch('Overlays', 'closeWindow');
  }

  onTransitionEnd = () => {
    this.clearState();
  }
}
